/**
 * Goods-purchase fields for one estimate line (server migration 217).
 *
 * Rendered under the line's description only when the work is a GOODS purchase:
 *   - Asset category: what the line becomes in the asset register when its bill is
 *     paid. "Not an asset" is a real choice (toner, a mouse), not a missing value.
 *   - Specification: carried to the purchase order and onto the asset's description.
 */

import { useEffect, useState } from 'react';
import axiosInstance from '@/services/interceptor';

export interface AssetCategoryOption {
  asst_ctgry_id: number;
  asst_ctgry_cd: string;
  asst_ctgry_nm: string;
  asst_type_cd?: string | null;
}

// One fetch per page load, shared by every line and every open of the form.
let categoriesPromise: Promise<AssetCategoryOption[]> | null = null;
const loadCategories = () => {
  if (!categoriesPromise) {
    categoriesPromise = axiosInstance
      .get('ds-wms/masters/asset-categories')
      .then((r: any) => (Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch((e: unknown) => {
        categoriesPromise = null; // allow a retry on the next open
        throw e;
      });
  }
  return categoriesPromise;
};

export function useAssetCategories(enabled: boolean) {
  const [categories, setCategories] = useState<AssetCategoryOption[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadCategories()
      .then((rows) => { if (alive) { setCategories(rows); setFailed(false); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [enabled]);
  return { categories, failed };
}

interface Props {
  categories: AssetCategoryOption[];
  asstCtgryId: number | null | undefined;
  spcfcnTx: string | undefined;
  onCategoryChange: (id: number | null) => void;
  onSpecChange: (value: string) => void;
}

export default function GoodsItemFields({ categories, asstCtgryId, spcfcnTx, onCategoryChange, onSpecChange }: Props) {
  return (
    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1">
      <select
        value={asstCtgryId ?? ''}
        onChange={(e) => onCategoryChange(e.target.value ? Number(e.target.value) : null)}
        className={`text-[11px] border rounded px-1.5 py-0.5 ${asstCtgryId ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-500'}`}
        title="Asset category this item becomes when its bill is paid"
      >
        <option value="">Not an asset (expense)</option>
        {categories.map((c) => (
          <option key={c.asst_ctgry_id} value={c.asst_ctgry_id}>
            Asset: {c.asst_ctgry_nm}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={spcfcnTx || ''}
        onChange={(e) => onSpecChange(e.target.value)}
        className="text-[11px] border border-gray-200 rounded px-1.5 py-0.5"
        placeholder="Specification (e.g. i5, 16 GB RAM, 512 GB SSD)"
        maxLength={1000}
      />
    </div>
  );
}
